import useApiRequest from "../../../../utils/hooks/useApiRequest";
import { useEffect, useState } from "react";
import { CalendarModel } from "../../../models/CalendarModel";
import { EXERCISE_BASKET_CLEAR, GET_CALENDAR_DATA, GET_EXERCISE_FOR_DATE } from "../../../api/apiRoutes";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { Exercise } from "../../../models/Exercise";
import { BasketItem } from "../../../models/BasketItem";
import { scrollToElement } from "../../../animations/scrollToElement";

const dateFormat = "yyyy-MM-dd";

interface MonthModel {
  firstDayInMonth: string;
  endDayOfMonth: string;
}

export default function useExerciseManager(basket: BasketItem[], setBasket: (value: BasketItem[]) => void, addExerciseToBasket: (value: Exercise) => void) {
  const { get, post } = useApiRequest();
  const [data, setData] = useState<CalendarModel[]>([]);
  const currentDate = new Date();
  const [month, setMonth] = useState<MonthModel>({
    firstDayInMonth: format(startOfMonth(currentDate), dateFormat),
    endDayOfMonth: format(endOfMonth(currentDate), dateFormat)
  });
  const [exerciseList, setExerciseList] = useState<Exercise[]>([]);
  const [activeSelectExercise, setActiveSelectExercise] = useState<Exercise | null>(null);

  const getCalendar = async () => {
    const { result, isError } = await get(GET_CALENDAR_DATA(month.firstDayInMonth, month.endDayOfMonth));
    if (!isError) {
      setData(result);
    }
  };

  const getPromotionPrice = (date: Date) => {
    const dateString = format(date, dateFormat);
    const price = data.find(item => item.date === dateString)?.discountedPrice;
    const freePlaces = data.find(item => item.date === dateString)?.freePlaces;
    if (price && price > 0) {
      return `${price.toFixed(2)} zł`;
    } else {
      if (freePlaces && freePlaces > 0) {
        return "Wybierz";
      }
    }
  };

  const getPrice = (date: Date) => {
    const dateString = format(date, dateFormat);
    const price = data.find(item => item.date === dateString)?.mainPrice;
    return price ? `${price.toFixed(2)} zł` : "";
  };

  const getDisabledDay = (date: Date) => {
    const dateString = format(date, dateFormat);
    const freePlaces = data.find(item => item.date === dateString)?.freePlaces;
    return freePlaces ? freePlaces <= 0 : true;
  };

  const onSelectDate = async (date: Date) => {
    window.parent.postMessage("resize::1500", "*");
    const dateString = format(date, dateFormat);
    const exerciseGroupIds = data.find(item => item.date === dateString)?.exerciseGroupIds;
    const { result, isError } = await get(GET_EXERCISE_FOR_DATE(dateString, exerciseGroupIds ? exerciseGroupIds : ""));
    if (!isError) {
      setExerciseList(result);
      if (result.length > 0) {
        scrollToElement("exercise-group-panel");
      }

      clearExerciseBasket(result);
    }
  };

  const clearExerciseBasket = async (exerciseList?: Exercise[]) => {
    const { isError, result } = await post(EXERCISE_BASKET_CLEAR);
    if (!isError) {
      getCalendar();
      setBasket(result.basket);
      setActiveSelectExercise(null);
      if (exerciseList) addWhenOneExercise(exerciseList);
    }
  };

  const changeMonth = (date: Date) => {
    setMonth({
      firstDayInMonth: format(startOfMonth(date), dateFormat),
      endDayOfMonth: format(endOfMonth(date), dateFormat)
    });
  };

  const getPokazujDO = (date: Date) => {
    const dateString = format(date, dateFormat);
    const pokazujDo = data.find(item => item.date === dateString)?.pokazujDo;
    return pokazujDo ? pokazujDo : false;
  };

  const getExerciseGroupIds = (date: Date) => {
    const dateString = format(date, dateFormat);
    const exerciseGroupIds = data.find(item => item.date === dateString)?.exerciseGroupIds;
    return exerciseGroupIds ? exerciseGroupIds : "";
  };

  const getSelectedDay = (date: Date): boolean => {
    const dateString = format(date, dateFormat);
    const selectedDay = data.find(item => item.date === dateString)?.selectedDay;
    return selectedDay ? selectedDay : false;
  };

  const addWhenOneExercise = (list: Exercise[]) => {
    if (list.length === 1) {
      setActiveSelectExercise(list[0]);
      addExerciseToBasket(list[0]);
    }
  };

  useEffect(() => {
    getCalendar();
    //eslint-disable-next-line
  }, [month, basket]);

  return {
    getPromotionPrice,
    getPrice,
    onSelectDate,
    exerciseList,
    changeMonth,
    getPokazujDO,
    getExerciseGroupIds,
    getSelectedDay,
    getDisabledDay,
    activeSelectExercise,
    setActiveSelectExercise
  };
}
